import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Lightbulb } from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";
import { Card } from "./primitives";

// Premium "How it works" guide card. steps: [string], bullets: [string], tip: string.
export function HowItWorks({ title = "How it works", steps = [], bullets = [], tip, icon }) {
  const t = useDashTheme();
  return (
    <Card padding={0} style={{ overflow: "hidden" }}>
      {/* gradient banner */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: `${t.s[4]}px ${t.s[5]}px`, background: t.c.primaryGradient, color: "#fff" }}>
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.18)" }}>
          {icon || <Sparkles size={18} />}
        </span>
        <div>
          <div style={{ fontSize: t.font.size.lg, fontWeight: 700, letterSpacing: "-0.01em" }}>{title}</div>
          <div style={{ fontSize: t.font.size.sm, opacity: 0.85 }}>Get an AI analysis in three simple steps</div>
        </div>
      </div>

      <div style={{ padding: t.s[5], display: "flex", flexDirection: "column", gap: t.s[4] }}>
        {/* steps */}
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(steps.length, 3)}, 1fr)`, gap: t.s[4] }} className="dashv2-hiw-steps">
          {steps.map((s, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.07, ease: t.motion.ease.out }}
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 99,
                background: t.c.primarySoft, color: t.c.primary, fontSize: t.font.size.sm, fontWeight: 700,
              }}>{i + 1}</span>
              <span style={{ fontSize: t.font.size.md, color: t.c.text, lineHeight: 1.5 }}>{s}</span>
            </motion.div>
          ))}
        </div>

        {/* what you get */}
        {bullets.length > 0 && (
          <div style={{ padding: t.s[4], borderRadius: t.r.md, background: t.c.surfaceMuted, border: `1px solid ${t.c.border}` }}>
            <div style={{ fontSize: t.font.size.xs, fontWeight: 600, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: t.s[3] }}>What you'll get</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: t.s[2] }}>
              {bullets.map((b, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: t.font.size.md, color: t.c.textSecondary }}>
                  <span style={{ width: 6, height: 6, borderRadius: 99, background: t.c.primary, marginTop: 8, flexShrink: 0 }} />
                  {b}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* tip */}
        {tip && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: t.s[3], borderRadius: t.r.md, background: t.status.successSoft }}>
            <Lightbulb size={16} style={{ color: t.status.positive, flexShrink: 0, marginTop: 2 }} />
            <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.5 }}>{tip}</span>
          </div>
        )}
      </div>
    </Card>
  );
}
